import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, UserPlus, LayoutGrid, TableIcon, ChevronDown, AlertTriangle, Download } from "lucide-react";
import { api } from "../api";
import type { Member } from "../types";

const STATUS_COLORS: Record<string, string> = {
  Active: "bg-emerald-100 text-emerald-700",
  Paused: "bg-amber-100 text-amber-700",
  "Payment Issues": "bg-red-100 text-red-700",
  "Expired/Former": "bg-gray-100 text-gray-500",
};

const MEMBERSHIP_COLORS: Record<string, string> = {
  Basic: "bg-blue-50 text-blue-700",
  VIP: "bg-pink-100 text-pink-700",
};

function isPastEnd(member: Member): boolean {
  if (!member.end_date) return false;
  const today = new Date().toISOString().split("T")[0];
  return member.end_date < today;
}

function fmtDate(d: string | null): string {
  if (!d) return "\u2014";
  // Normalize ISO timestamps to YYYY-MM-DD
  let ds = d;
  if (d.includes("T")) {
    const parsed = new Date(d);
    ds = isNaN(parsed.getTime()) ? d : parsed.toISOString().split("T")[0];
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ds)) return d;
  const [y, m, dd] = ds.split("-");
  return `${dd}/${m}/${y}`;
}

export function Members() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [view, setView] = useState<"cards" | "table">("cards");
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterType, setFilterType] = useState(searchParams.get("membership_type") || "");
  const [filterPlan, setFilterPlan] = useState(searchParams.get("payment_plan") || "");
  const [filterStatus, setFilterStatus] = useState(searchParams.get("status") || "");

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getMembers({
        search: search || undefined,
        membership_type: filterType || undefined,
        payment_plan: filterPlan || undefined,
        status: filterStatus || undefined,
      });
      setMembers(res.members);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, filterType, filterPlan, filterStatus]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleFilterStatus = (s: string) => {
    setFilterStatus(s);
    if (s) setSearchParams({ status: s }); else setSearchParams({});
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Members</h1>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button onClick={() => setView("cards")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "cards" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}><LayoutGrid className="w-4 h-4" /> Cards</button>
            <button onClick={() => setView("table")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "table" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}><TableIcon className="w-4 h-4" /> Table</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={async () => { const res = await api.exportMembersCsv(); const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "members.csv"; a.click(); URL.revokeObjectURL(url); }} className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-[#5f1b2c] hover:bg-[#4a1520] text-white rounded-lg text-sm font-medium transition-colors">
              <UserPlus className="w-4 h-4" /> Add Member
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search by name..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#5f1b2c] focus:ring-1 focus:ring-[#5f1b2c]/30 text-sm shadow-sm" />
        </div>
        <select value={filterStatus} onChange={(e) => handleFilterStatus(e.target.value)} className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-[#5f1b2c]">
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Paused">Paused</option>
          <option value="Payment Issues">Payment Issues</option>
          <option value="Expired/Former">Expired / Former</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-[#5f1b2c]">
          <option value="">All Types</option>
          <option value="Basic">Basic</option>
          <option value="VIP">VIP</option>
        </select>
        <select value={filterPlan} onChange={(e) => setFilterPlan(e.target.value)} className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-[#5f1b2c]">
          <option value="">All Plans</option>
          <option value="Free">Free</option>
          <option value="Week-to-week subscription">Week-to-week</option>
          <option value="12 week membership">12 Week</option>
          <option value="6 month membership">6 Month</option>
          <option value="12 month membership">12 Month</option>
        </select>
      </div>

      {loading ? (
        <div className="space-y-3">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-16 bg-gray-50 rounded-lg animate-pulse" />)}</div>
      ) : members.length === 0 ? (
        <div className="text-center py-20 text-gray-400"><p className="text-lg">No members found</p></div>
      ) : view === "table" ? (
        <TableView members={members} onOpen={(m) => navigate(`/members/${m.id}`)} />
      ) : (
        <CardView members={members} onOpen={(m) => navigate(`/members/${m.id}`)} />
      )}

      {showAddModal && (
        <AddMemberModal onClose={() => setShowAddModal(false)} onCreated={() => { setShowAddModal(false); loadMembers(); }} />
      )}
    </div>
  );
}

function CardView({ members, onOpen }: { members: Member[]; onOpen: (m: Member) => void }) {
  return (
    <div className="space-y-2">
      {members.map((m) => (
        <button key={m.id} onClick={() => onOpen(m)} className={`w-full text-left bg-white border rounded-xl p-4 hover:shadow-md transition-all ${m.membership_type === "VIP" ? "border-pink-200 bg-pink-50/50 hover:border-pink-300" : "border-gray-200 hover:border-[#e2c4b6]"}`}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#e2c4b6]/40 flex items-center justify-center text-[#5f1b2c] font-bold text-sm shrink-0">
              {m.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900 truncate">{m.name}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[m.status] || "bg-gray-100 text-gray-600"}`}>{m.status}</span>
                {m.membership_type && <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${MEMBERSHIP_COLORS[m.membership_type] || "bg-gray-100 text-gray-600"}`}>{m.membership_type}</span>}
                {isPastEnd(m) && (m.status === "Paused" || m.status === "Payment Issues") && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> End date passed</span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                {m.payment_plan && <span>{m.payment_plan}</span>}
                {m.email && <span className="truncate max-w-[200px]">{m.email}</span>}
                {m.phone && <span>{m.phone}</span>}
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
          </div>
        </button>
      ))}
    </div>
  );
}

function TableView({ members, onOpen }: { members: Member[]; onOpen: (m: Member) => void }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-100">
              <th className="px-4 py-3 font-medium">Member</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Payment Plan</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Coach</th>
              <th className="px-4 py-3 font-medium">End Date</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} onClick={() => onOpen(m)} className={`border-b cursor-pointer transition-colors ${m.membership_type === "VIP" ? "bg-pink-50/50 border-pink-100 hover:bg-pink-50" : "border-gray-50 hover:bg-gray-50/50"}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#e2c4b6]/40 flex items-center justify-center text-[#5f1b2c] font-bold text-xs shrink-0">
                      {m.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <span className="font-medium text-gray-900">{m.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${MEMBERSHIP_COLORS[m.membership_type || ""] || "bg-gray-100 text-gray-600"}`}>{m.membership_type || "\u2014"}</span></td>
                <td className="px-4 py-3 text-gray-500 text-xs">{m.payment_plan || "\u2014"}</td>
                <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[m.status] || "bg-gray-100 text-gray-600"}`}>{m.status}</span></td>
                <td className="px-4 py-3 text-gray-500 text-xs">{m.coach_assigned || "\u2014"}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(m.end_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddMemberModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", payment_plan: "", membership_type: "Basic",
    start_date: "", end_date: "", coach_assigned: "", goals: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.createMember({
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        payment_plan: form.payment_plan || null,
        membership_type: form.membership_type || "Basic",
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        coach_assigned: form.coach_assigned || null,
        goals: form.goals || null,
        notes: form.notes || null,
      });
      onCreated();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-900">Add New Member</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input required placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c] focus:ring-1 focus:ring-[#5f1b2c]/30" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c]" />
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={form.payment_plan} onChange={(e) => setForm({ ...form, payment_plan: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c]">
              <option value="">Payment Plan</option>
              <option value="Free">Free</option>
              <option value="Week-to-week subscription">Week-to-week</option>
              <option value="6 month membership">6 Month</option>
              <option value="12 month membership">12 Month</option>
              <option value="12 week membership">12 Week</option>
            </select>
            <select value={form.membership_type} onChange={(e) => setForm({ ...form, membership_type: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c]">
              <option value="Basic">Basic</option>
              <option value="VIP">VIP</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c]" />
            <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c]" />
          </div>
          <select value={form.coach_assigned} onChange={(e) => setForm({ ...form, coach_assigned: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c]">
            <option value="">Coach Assigned</option>
            <option value="Henny">Henny</option>
            <option value="Lily">Lily</option>
          </select>
          <textarea placeholder="Goals" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} rows={2} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c] resize-none" />
          <textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#5f1b2c] resize-none" />
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200 transition-colors font-medium">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 bg-[#5f1b2c] text-white rounded-lg text-sm font-medium hover:bg-[#4a1520] transition-colors disabled:opacity-50">{saving ? "Saving..." : "Add Member"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}